/**
 * Master data (Phase 1): enums, validation schemas and response types for
 * partners, items, categories, units, warehouses, cost centres, VAT codes,
 * payment terms, salespersons, bank accounts, cheque books and employees.
 * The same schemas validate the forms and the API.
 */
import { z } from 'zod';
import { always, optionalEmail } from './schemas';

// ---------- Helpers ----------

const req = (label: string, max: number) =>
  z.string().trim().min(1, `${label} is required`).max(max, `${label} must be at most ${max} characters`);
const opt = (max: number) => z.string().trim().max(max, `At most ${max} characters`).optional().default('');
const uuidReq = (msg: string) => z.string({ error: msg }).uuid(msg);
const uuidOpt = z.string().uuid().nullable().optional().transform((v) => v ?? null);
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Enter a valid date');
const optDate = z.union([z.literal(''), isoDate]).nullable().optional().transform((v) => v || null);
/** Optional non-negative number; '' and null mean "not set". */
const optAmount = (label: string) =>
  z.preprocess(
    (v) => (v === '' || v === undefined ? null : v),
    z.coerce.number({ error: `Enter a valid ${label}` }).min(0, `${label} cannot be negative`).max(1e12).nullable(),
  );
/** Lead time in whole days (0–365), empty = not set. */
export const leadTimeDaysSchema = z.preprocess(
  (v) => (v === '' || v === undefined ? null : v),
  z.coerce.number({ error: 'Enter the lead time in days' }).int('Lead time is whole days').min(0, 'Lead time cannot be negative').max(365, 'Lead time is at most 365 days').nullable(),
);
const lookupCode = z
  .string()
  .trim()
  .min(1, 'Code is required')
  .max(20, 'Code must be at most 20 characters')
  .regex(/^[A-Z0-9][A-Z0-9._/-]*$/, 'Use uppercase letters, numbers, dot, dash or slash');

/** Phone numbers in local or international form (suppliers may be abroad). */
export const phoneSchema = z
  .string()
  .trim()
  .regex(/^\+?[0-9 ()-]{7,20}$/, 'Enter a valid phone number, e.g. +971501234567')
  .or(z.literal(''));

/** UAE TRN: 15 digits starting with 100. */
export const uaeTrnSchema = z
  .string()
  .trim()
  .regex(/^100\d{12}$/, 'TRN must be 15 digits and start with 100')
  .or(z.literal(''));

/** Emirates ID: 784-YYYY-NNNNNNN-C, validated with the Luhn check digit. */
export function isValidEmiratesId(v: string): boolean {
  const d = v.replace(/-/g, '');
  if (!/^784\d{12}$/.test(d)) return false;
  let sum = 0;
  for (let i = 0; i < d.length; i++) {
    let n = Number(d[d.length - 1 - i]);
    if (i % 2 === 1) {
      n *= 2;
      if (n > 9) n -= 9;
    }
    sum += n;
  }
  return sum % 10 === 0;
}
/** As typed: digits only, dashes put in after 784, the year and the 7-digit number (784-YYYY-NNNNNNN-C). */
export function formatEmiratesId(raw: string): string {
  const d = raw.replace(/\D/g, '').slice(0, 15);
  return [d.slice(0, 3), d.slice(3, 7), d.slice(7, 14), d.slice(14)].filter(Boolean).join('-');
}
export const emiratesIdSchema = z
  .string()
  .trim()
  .refine((v) => v === '' || /^784\d{12}$/.test(v.replace(/-/g, '')), 'An Emirates ID is 15 digits starting with 784: 784-YYYY-NNNNNNN-C')
  .refine((v) => v === '' || !/^784\d{12}$/.test(v.replace(/-/g, '')) || isValidEmiratesId(v), 'This Emirates ID is not valid: check the digits against the card (the last digit is a check digit)')
  .transform((v) => {
    const d = v.replace(/-/g, '');
    return d ? `${d.slice(0, 3)}-${d.slice(3, 7)}-${d.slice(7, 14)}-${d.slice(14)}` : '';
  });

/** IBAN with the ISO 13616 mod-97 check; UAE IBANs are AE + 21 digits. */
export function isValidIban(v: string): boolean {
  const s = v.replace(/\s/g, '').toUpperCase();
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{10,30}$/.test(s)) return false;
  if (s.startsWith('AE') && s.length !== 23) return false;
  const moved = s.slice(4) + s.slice(0, 4);
  let rem = 0;
  for (const ch of moved) {
    const val = /\d/.test(ch) ? ch : String(ch.charCodeAt(0) - 55);
    for (const digit of val) rem = (rem * 10 + Number(digit)) % 97;
  }
  return rem === 1;
}
export const ibanSchema = z
  .string()
  .trim()
  .refine((v) => v === '' || isValidIban(v), 'Enter a valid IBAN, e.g. AE07 0331 2345 6789 0123 456')
  .transform((v) => v.replace(/\s/g, '').toUpperCase());

/** Normalised name used to find likely duplicates (case, punctuation, legal suffixes ignored). */
export function duplicateKey(name: string): string {
  return name
    .toUpperCase()
    .replace(/&AMP;/g, '&')
    .replace(/\b(L\.?\s?L\.?\s?C|F\.?Z\.?\s?E|F\.?Z\.?\s?C\.?O|FZ\s?LLC|EST|ESTABLISHMENT|CO|COMPANY|TRADING|TR|MR|MRS|MS|MISS)\b\.?/g, ' ')
    .replace(/[^A-Z0-9]/g, '');
}

// ---------- Enumerations ----------

const labels = <T extends string>(m: Record<T, string>) => m;

export const EMIRATES = ['AUH', 'DXB', 'SHJ', 'AJM', 'UAQ', 'RAK', 'FUJ', 'OUTSIDE_UAE'] as const;
export type Emirate = (typeof EMIRATES)[number];
export const EMIRATE_LABELS = labels<Emirate>({
  AUH: 'Abu Dhabi',
  DXB: 'Dubai',
  SHJ: 'Sharjah',
  AJM: 'Ajman',
  UAQ: 'Umm Al Quwain',
  RAK: 'Ras Al Khaimah',
  FUJ: 'Fujairah',
  OUTSIDE_UAE: 'Outside UAE (export)',
});

/**
 * Two kinds of customer (owner, 2026-10-23). Regular: its own account (new ones under 102039 Trade Receivables).
 * One-time: posts to its salesperson's account. Every customer has a salesperson.
 */
export const CUSTOMER_TYPES = ['REGULAR', 'ONE_TIME'] as const;
export type CustomerType = (typeof CUSTOMER_TYPES)[number];
export const CUSTOMER_TYPE_LABELS = labels<CustomerType>({
  REGULAR: 'Regular',
  ONE_TIME: 'One-time',
});

/**
 * VAT scenario of a customer (owner, 2026-11-02). Ruby Foam sells from the mainland (Al Jurf, Ajman), so only the
 * "Mainland →" scenarios apply. The scenario decides the VAT of every quotation and order: 5% (prices exclusive or
 * inclusive of VAT) or zero-rated 0% (needs export / exit proof within 90 days, else 5% is due).
 */
export const VAT_SCENARIOS = ['MAINLAND_MAINLAND', 'MAINLAND_INDIVIDUAL', 'SERVICES_UAE', 'MAINLAND_DZ_EXPORT', 'EXPORT_OUTSIDE_UAE'] as const;
export type VatScenario = (typeof VAT_SCENARIOS)[number];
export const VAT_SCENARIO_LABELS = labels<VatScenario>({
  MAINLAND_MAINLAND: 'Mainland → Mainland (5%)',
  MAINLAND_INDIVIDUAL: 'Mainland → Individual (5%)',
  SERVICES_UAE: 'Services anywhere in UAE (5%)',
  MAINLAND_DZ_EXPORT: 'Mainland → Designated Zone, exported goods (0%)',
  EXPORT_OUTSIDE_UAE: 'Export outside UAE (0%)',
});
/** Tax code each scenario charges: SR (standard 5%) or ZR (zero-rated 0%). */
export const VAT_SCENARIO_TAX: Record<VatScenario, 'SR' | 'ZR'> = {
  MAINLAND_MAINLAND: 'SR',
  MAINLAND_INDIVIDUAL: 'SR',
  SERVICES_UAE: 'SR',
  MAINLAND_DZ_EXPORT: 'ZR',
  EXPORT_OUTSIDE_UAE: 'ZR',
};
export const isZeroRatedScenario = (s: VatScenario | null | undefined) => !!s && VAT_SCENARIO_TAX[s] === 'ZR';
/** Days to obtain the export / exit proof of a zero-rated supply. */
export const EXPORT_PROOF_DAYS = 90;

export const SUPPLIER_GROUPS = ['TRADE_CASH', 'TRADE_CREDIT', 'SERVICE', 'OTHER'] as const;
export type SupplierGroup = (typeof SUPPLIER_GROUPS)[number];
export const SUPPLIER_GROUP_LABELS = labels<SupplierGroup>({
  TRADE_CASH: 'Trade creditor – cash supplies',
  TRADE_CREDIT: 'Trade creditor – credit supplies',
  SERVICE: 'Service provider',
  OTHER: 'Other',
});

export const ITEM_TYPES = ['RAW_MATERIAL', 'CONSUMABLE', 'SEMI_FINISHED', 'FINISHED_GOOD', 'TRADING_GOOD', 'SERVICE'] as const;
export type ItemType = (typeof ITEM_TYPES)[number];
export const ITEM_TYPE_LABELS = labels<ItemType>({
  RAW_MATERIAL: 'Raw material',
  CONSUMABLE: 'Consumable',
  SEMI_FINISHED: 'Semi-finished',
  FINISHED_GOOD: 'Finished good',
  TRADING_GOOD: 'Trading good',
  SERVICE: 'Service',
});

export const CATEGORY_KINDS = ['MATERIAL', 'PRODUCT', 'SERVICE'] as const;
export type CategoryKind = (typeof CATEGORY_KINDS)[number];
export const CATEGORY_KIND_LABELS = labels<CategoryKind>({ MATERIAL: 'Materials', PRODUCT: 'Products', SERVICE: 'Services' });
/** Which category kind each item type belongs under. */
export const ITEM_TYPE_CATEGORY: Record<ItemType, CategoryKind> = {
  RAW_MATERIAL: 'MATERIAL',
  CONSUMABLE: 'MATERIAL',
  SEMI_FINISHED: 'PRODUCT',
  FINISHED_GOOD: 'PRODUCT',
  TRADING_GOOD: 'PRODUCT',
  SERVICE: 'SERVICE',
};

export const SOURCING = ['LOCAL', 'IMPORTED', 'BOTH'] as const;
export type Sourcing = (typeof SOURCING)[number];
export const SOURCING_LABELS = labels<Sourcing>({ LOCAL: 'Local', IMPORTED: 'Imported', BOTH: 'Local and imported' });

export const ITEM_ALIAS_KINDS = ['LEGACY_RM', 'LEGACY_FG', 'LEGACY_REF', 'BARCODE', 'MERGED'] as const;
export type ItemAliasKind = (typeof ITEM_ALIAS_KINDS)[number];
export const ITEM_ALIAS_LABELS = labels<ItemAliasKind>({
  LEGACY_RM: 'Old material code',
  LEGACY_FG: 'Old product code',
  LEGACY_REF: 'Old reference no.',
  BARCODE: 'Barcode',
  MERGED: 'Code of merged item',
});

export const WAREHOUSE_TYPES = ['RAW_MATERIAL', 'FINISHED_GOODS', 'WIP', 'GENERAL', 'TRANSIT'] as const;
export type WarehouseType = (typeof WAREHOUSE_TYPES)[number];
export const WAREHOUSE_TYPE_LABELS = labels<WarehouseType>({
  RAW_MATERIAL: 'Raw material store',
  FINISHED_GOODS: 'Finished goods store',
  WIP: 'Work in progress (production floor)',
  GENERAL: 'General store',
  TRANSIT: 'In transit',
});

export const TAX_KINDS = ['STANDARD', 'ZERO_RATED', 'EXEMPT', 'OUT_OF_SCOPE'] as const;
export type TaxKind = (typeof TAX_KINDS)[number];
export const TAX_KIND_LABELS = labels<TaxKind>({
  STANDARD: 'Standard rated',
  ZERO_RATED: 'Zero rated',
  EXEMPT: 'Exempt',
  OUT_OF_SCOPE: 'Out of scope',
});

export const PAYMENT_TERM_KINDS = ['CASH', 'ADVANCE', 'CREDIT'] as const;
export type PaymentTermKind = (typeof PAYMENT_TERM_KINDS)[number];
export const PAYMENT_TERM_KIND_LABELS = labels<PaymentTermKind>({ CASH: 'Cash', ADVANCE: 'Advance', CREDIT: 'Credit' });

export const BANK_ACCOUNT_KINDS = ['BANK', 'GATEWAY'] as const;
export type BankAccountKind = (typeof BANK_ACCOUNT_KINDS)[number];
export const BANK_ACCOUNT_KIND_LABELS = labels<BankAccountKind>({ BANK: 'Bank account', GATEWAY: 'Online payment gateway' });

export const GENDERS = ['MALE', 'FEMALE'] as const;
export const GENDER_LABELS = labels<(typeof GENDERS)[number]>({ MALE: 'Male', FEMALE: 'Female' });
export const MARITAL_STATUSES = ['SINGLE', 'MARRIED', 'DIVORCED', 'WIDOWED'] as const;
export const MARITAL_LABELS = labels<(typeof MARITAL_STATUSES)[number]>({ SINGLE: 'Single', MARRIED: 'Married', DIVORCED: 'Divorced', WIDOWED: 'Widowed' });
export const LABOUR_TYPES = ['DIRECT', 'INDIRECT'] as const;
export const LABOUR_TYPE_LABELS = labels<(typeof LABOUR_TYPES)[number]>({ DIRECT: 'Direct labour', INDIRECT: 'Indirect labour' });
export const EMPLOYEE_STATUSES = ['ACTIVE', 'LEFT'] as const;
export type EmployeeStatus = (typeof EMPLOYEE_STATUSES)[number];
export const EMPLOYEE_STATUS_LABELS = labels<EmployeeStatus>({ ACTIVE: 'Active', LEFT: 'Left' });
export const VISA_TYPES = ['COMPANY', 'OWN', 'FAMILY', 'VISIT', 'GOLDEN', 'UAE_NATIONAL', 'OTHER'] as const;
export const VISA_TYPE_LABELS = labels<(typeof VISA_TYPES)[number]>({
  COMPANY: 'Company visa',
  OWN: 'Own visa',
  FAMILY: 'Family sponsorship',
  VISIT: 'Visit visa',
  GOLDEN: 'Golden visa',
  UAE_NATIONAL: 'UAE national',
  OTHER: 'Other',
});

/** Record status shared by masters; MERGED records are kept read-only for history. */
export const MASTER_STATUSES = ['ACTIVE', 'INACTIVE', 'MERGED'] as const;
export type MasterStatus = (typeof MASTER_STATUSES)[number];

// ---------- Master code series (immutable, server-generated) ----------

export interface MasterSeriesDef {
  key: string;
  label: string;
  prefix: string;
  digits: number;
}
export const MASTER_SERIES = {
  customer: { key: 'mdm.customer', label: 'Customer code', prefix: 'C', digits: 5 },
  supplier: { key: 'mdm.supplier', label: 'Supplier code', prefix: 'S', digits: 5 },
  item: { key: 'mdm.item', label: 'Item code', prefix: 'IT', digits: 5 },
  product: { key: 'mdm.product', label: 'Product model code', prefix: 'PM', digits: 4 },
  catalogue: { key: 'mdm.catalogue', label: 'Catalogue code', prefix: 'CAT', digits: 5 },
  salesperson: { key: 'mdm.salesperson', label: 'Salesperson code', prefix: 'SP', digits: 3 },
  employee: { key: 'hr.employee', label: 'Employee number', prefix: '', digits: 1 },
  designation: { key: 'hr.designation', label: 'Designation code', prefix: 'DES', digits: 4 },
} as const satisfies Record<string, MasterSeriesDef>;
export const formatMasterCode = (s: MasterSeriesDef, n: number) =>
  s.prefix ? `${s.prefix}-${String(n).padStart(s.digits, '0')}` : String(n).padStart(s.digits, '0');

// ---------- Simple lookups ----------

export const uomSchema = z.object({
  code: z.string().trim().min(1, 'Code is required').max(10, 'At most 10 characters').regex(/^[A-Z0-9]+$/, 'Use uppercase letters and numbers'),
  name: req('Name', 40),
  decimals: z.coerce.number({ error: 'Enter 0–4' }).int('Whole number').min(0, 'At least 0').max(4, 'At most 4'),
  isActive: z.boolean(),
});
export type UomInput = z.infer<typeof uomSchema>;

export const costCentreSchema = z.object({
  code: lookupCode,
  name: req('Name', 80),
  parentId: uuidOpt,
  isActive: z.boolean(),
});
export type CostCentreInput = z.infer<typeof costCentreSchema>;

export const taxCodeSchema = z
  .object({
    code: lookupCode,
    name: req('Name', 80),
    kind: z.enum(TAX_KINDS, { error: 'Select the VAT treatment' }),
    rate: z.coerce.number({ error: 'Enter the rate' }).min(0, 'Cannot be negative').max(100, 'At most 100%'),
    isActive: z.boolean(),
  })
  .refine((v) => (v.kind === 'STANDARD' ? v.rate > 0 : v.rate === 0), {
    path: ['rate'],
    message: 'Standard rated codes need a rate above 0; other kinds must be 0%',
    ...always,
  });
export type TaxCodeInput = z.infer<typeof taxCodeSchema>;

export const paymentTermSchema = z
  .object({
    code: lookupCode,
    name: req('Name', 80),
    kind: z.enum(PAYMENT_TERM_KINDS, { error: 'Select a kind' }),
    days: z.coerce.number({ error: 'Enter the number of days' }).int('Whole days').min(0, 'Cannot be negative').max(365, 'At most 365 days'),
    isActive: z.boolean(),
  })
  .refine((v) => (v.kind === 'CREDIT' ? v.days > 0 : v.days === 0), {
    path: ['days'],
    message: 'Credit terms need at least 1 day; cash and advance terms are 0 days',
    ...always,
  });
export type PaymentTermInput = z.infer<typeof paymentTermSchema>;

export const warehouseSchema = z.object({
  code: lookupCode,
  name: req('Name', 80),
  type: z.enum(WAREHOUSE_TYPES, { error: 'Select a type' }),
  branchId: uuidReq('Select a branch'),
  isActive: z.boolean(),
});
export type WarehouseInput = z.infer<typeof warehouseSchema>;

export const designationSchema = z.object({
  /** Made automatically (DES-0001…) when blank; never typed (owner, 2026-10-27). */
  code: z.union([z.literal(''), lookupCode]).optional().default(''),
  title: req('Title', 80),
  departmentId: uuidOpt,
  /** Seniority inside a department: lower is listed first (1 = most senior). */
  rank: z.coerce.number({ error: 'Enter a number' }).int('Whole number').min(0).max(999).default(0),
  isActive: z.boolean(),
});
export type DesignationInput = z.infer<typeof designationSchema>;

export const itemCategorySchema = z.object({
  code: lookupCode,
  name: req('Name', 80),
  kind: z.enum(CATEGORY_KINDS, { error: 'Select a kind' }),
  parentId: uuidOpt,
  costCentreId: uuidOpt,
  /** Goods receipts of this category get a quality-check step (accepted / rejected quantity). */
  requiresInspection: z.boolean().default(false),
  requiresInstallation: z.boolean().default(false),
  /** Warranty on finished goods (months); empty = as the parent category, then the setting. */
  warrantyMonths: z.coerce.number().int().min(0).max(240).nullable().default(null),
  /** Goods receipts may take up to this % more than the PO line; empty = as the parent, then Purchasing rules. */
  overReceiptPct: z.preprocess((v) => (v === '' || v == null ? null : v), z.coerce.number({ error: 'Enter a percentage' }).min(0, 'Cannot be negative').max(50, 'At most 50%').nullable()).default(null),
  /** Accounts; empty = inherit from the parent category, then the posting-account default. */
  purchaseAccountId: uuidOpt,
  inventoryAccountId: uuidOpt,
  isActive: z.boolean(),
});
export type ItemCategoryInput = z.infer<typeof itemCategorySchema>;

// ---------- Partners (customers & suppliers) ----------

export const partnerContactSchema = z.object({
  id: z.string().uuid().optional(),
  name: req('Contact name', 120),
  designation: opt(80),
  email: optionalEmail,
  phone: phoneSchema.optional().default(''),
  isPrimary: z.boolean().default(false),
});
export type PartnerContactInput = z.infer<typeof partnerContactSchema>;

export const customerProfileSchema = z
  .object({
    customerType: z.enum(CUSTOMER_TYPES, { error: 'Select a customer type' }),
    paymentTermId: uuidReq('Select payment terms'),
    vatScenario: z.enum(VAT_SCENARIOS, { error: 'Select the VAT scenario' }),
    /** Set from the VAT scenario on save (SR or ZR); kept for older callers. */
    taxCodeId: uuidOpt,
    placeOfSupply: z.enum(EMIRATES).nullable().optional().transform((v) => v ?? null),
    creditLimit: optAmount('credit limit'),
    creditHold: z.boolean().default(false),
    salespersonId: uuidReq('Select the salesperson'),
    branchId: uuidOpt,
  })
  .refine((v) => v.customerType !== 'REGULAR' || !!v.placeOfSupply, {
    path: ['placeOfSupply'],
    message: 'Select the emirate (place of supply) for VAT',
    ...always,
  });

export const supplierProfileSchema = z.object({
  supplierGroup: z.enum(SUPPLIER_GROUPS, { error: 'Select a supplier group' }),
  paymentTermId: uuidReq('Select payment terms'),
  taxCodeId: uuidReq('Select a VAT code'),
  /** New supplier: the head its own account is created under (6-digit code; owner, 2026-10-27). Ignored once it has one. */
  accountHead: z.string().regex(/^2\d{5}$/, 'Choose a supplier head').nullable().optional().transform((v) => v ?? null),
  /** Days from ordering to arrival from this supplier; wins over the item's lead time. */
  leadTimeDays: leadTimeDaysSchema.optional().transform((v) => v ?? null),
});

const partnerBase = z.object({
  name: req('Name', 160),
  legalName: opt(200),
  trn: uaeTrnSchema.optional().default(''),
  email: optionalEmail,
  phone: phoneSchema.optional().default(''),
  mobile: phoneSchema.optional().default(''),
  address: opt(400),
  shippingAddress: opt(400),
  city: opt(80),
  emirate: z.enum(EMIRATES).nullable().optional().transform((v) => v ?? null),
  country: z.string().trim().regex(/^[A-Z]{2}$/, 'Select a country').default('AE'),
  notes: opt(1000),
  isActive: z.boolean(),
  customer: customerProfileSchema.nullable().optional().transform((v) => v ?? null),
  supplier: supplierProfileSchema.nullable().optional().transform((v) => v ?? null),
  contacts: z.array(partnerContactSchema).max(20, 'At most 20 contacts').default([]),
});

const partnerRules = <T extends z.ZodType<z.infer<typeof partnerBase>>>(s: T) =>
  s
    .refine((v) => !!v.customer || !!v.supplier, { path: ['name'], message: 'A partner must be a customer, a supplier or both', ...always })
    .refine((v) => v.contacts.filter((c) => c.isPrimary).length <= 1, { path: ['contacts'], message: 'Only one contact can be primary', ...always })
    .refine((v) => v.country !== 'AE' || !v.emirate || v.emirate !== 'OUTSIDE_UAE', {
      path: ['emirate'],
      message: 'Choose an emirate for a UAE address',
      ...always,
    });

export const createPartnerSchema = partnerRules(partnerBase);
export type CreatePartnerInput = z.infer<typeof createPartnerSchema>;
export const updatePartnerSchema = partnerRules(partnerBase.extend({ version: z.number().int() }));
export type UpdatePartnerInput = z.infer<typeof updatePartnerSchema>;

export const mergeSchema = z.object({
  sourceId: z.string().uuid('Select the record to merge'),
  version: z.number().int(),
});
export type MergeInput = z.infer<typeof mergeSchema>;

export const dismissDuplicateSchema = z.object({ aId: z.string().uuid(), bId: z.string().uuid() });

// ---------- Salespersons ----------

export const salespersonSchema = z.object({
  name: req('Name', 120),
  displayName: opt(40),
  email: optionalEmail,
  phone: phoneSchema.optional().default(''),
  employeeId: uuidOpt,
  userId: uuidOpt,
  branchId: uuidOpt,
  commissionPct: z.preprocess((v) => (v === '' || v == null ? 0 : v), z.coerce.number().min(0, 'Cannot be negative').max(100, 'At most 100%')).default(0),
  /** The salesperson's own account: one-time customers' sales and receipts post here. */
  accountId: uuidOpt,
  /** The same person's customer record, when they also buy in their own name. */
  partnerId: uuidOpt,
  isActive: z.boolean(),
});
export type SalespersonInput = z.infer<typeof salespersonSchema>;

// ---------- Items & product models ----------

export const productSchema = z.object({
  name: req('Name', 160),
  categoryId: uuidReq('Select a category'),
  costCentreId: uuidReq('Select a cost centre'),
  description: opt(1000),
  collection: opt(80).default(''),
  material: opt(80).default(''),
  finish: opt(80).default(''),
  isCustom: z.boolean().default(false),
  /** Installed at the customer's site; null = as its category says. */
  installation: z.boolean().nullable().default(null),
  /** Warranty in months; null = as its category says. */
  warrantyMonths: z.coerce.number().int().min(0).max(240).nullable().default(null),
  isActive: z.boolean(),
  /** Product-level attribute values by attribute id ('' clears). */
  attributes: z.record(z.string().uuid(), z.string().max(200)).default({}),
});
export type ProductInput = z.infer<typeof productSchema>;

const optDim = (label: string) =>
  z.preprocess(
    (v) => (v === '' || v === undefined ? null : v),
    z.coerce.number({ error: `Enter a valid ${label}` }).positive(`${label} must be above 0`).max(100000).nullable(),
  );

const itemBase = z.object({
  name: req('Name', 200),
  type: z.enum(ITEM_TYPES, { error: 'Select an item type' }),
  categoryId: uuidReq('Select a category'),
  productId: uuidOpt,
  baseUomId: uuidReq('Select a unit of measure'),
  costCentreId: uuidReq('Select a cost centre'),
  departmentId: uuidOpt,
  description: opt(1000),
  hsCode: z.string().trim().regex(/^(\d{6,12})?$/, 'HS code is 6 to 12 digits').optional().default(''),
  sourcing: z.enum(SOURCING).default('LOCAL'),
  isStockTracked: z.boolean(),
  lengthCm: optDim('length'),
  widthCm: optDim('width'),
  heightCm: optDim('height'),
  colour: opt(40),
  standardSellingRate: optAmount('selling rate'),
  minQty: optAmount('minimum quantity'),
  maxQty: optAmount('maximum quantity'),
  reorderQty: optAmount('reorder quantity'),
  /** Days from ordering to arrival (default for every supplier; a supplier's own lead time wins). */
  leadTimeDays: leadTimeDaysSchema.optional().transform((v) => v ?? null),
  isGeneral: z.boolean().default(false),
  /** Where it is kept: warehouse and shelf / bin. */
  defaultWarehouseId: uuidOpt,
  shelfId: uuidOpt,
  purchaseUomId: uuidOpt,
  purchaseFactor: z.preprocess(
    (v) => (v === '' || v === undefined ? null : v),
    z.coerce.number({ error: 'Enter how many stock units one purchase unit holds' }).positive('Must be above 0').max(1_000_000).nullable(),
  ),
  barcode: z.string().trim().regex(/^([0-9A-Za-z-]{4,40})?$/, 'Barcode is 4–40 letters or digits').optional().default(''),
  isActive: z.boolean(),
});

const itemRules = <T extends z.ZodType<z.infer<typeof itemBase>>>(s: T) =>
  s
    .refine((v) => v.type !== 'SERVICE' || !v.isStockTracked, { path: ['isStockTracked'], message: 'Services are not stock items', ...always })
    .refine((v) => !v.productId || ['FINISHED_GOOD', 'SEMI_FINISHED', 'TRADING_GOOD'].includes(v.type), {
      path: ['productId'],
      message: 'Only finished, semi-finished and trading goods belong to a product model',
      ...always,
    })
    .refine((v) => v.minQty == null || v.maxQty == null || v.maxQty >= v.minQty, {
      path: ['maxQty'],
      message: 'Maximum must be at least the minimum quantity',
      ...always,
    })
    .refine((v) => !v.purchaseUomId || v.purchaseFactor != null, { path: ['purchaseFactor'], message: 'Say how many stock units one purchase unit holds', ...always })
    .refine((v) => v.purchaseUomId || v.purchaseFactor == null, { path: ['purchaseUomId'], message: 'Choose the purchase unit, or clear the quantity', ...always })
    .refine((v) => !v.purchaseUomId || v.purchaseUomId !== v.baseUomId, { path: ['purchaseUomId'], message: 'Same as the stock unit: leave it empty', ...always });

/**
 * A new item may bring its opening stock (before go-live): it becomes an Opening Stock document waiting for
 * approval, at the cost given, into the item's warehouse.
 */
export const createItemSchema = itemRules(
  itemBase.extend({
    openingQty: z.preprocess((v) => (v === '' || v === undefined ? null : v), z.coerce.number().positive('Must be above 0').max(1e9).nullable()).default(null),
    openingCost: z.preprocess((v) => (v === '' || v === undefined ? null : v), z.coerce.number().min(0, 'Cannot be negative').max(1e9).nullable()).default(null),
  }),
)
  .refine((v) => v.openingQty == null || v.openingCost != null, { path: ['openingCost'], message: 'Enter the cost per unit of the opening stock', ...always })
  .refine((v) => v.openingQty == null || !!v.defaultWarehouseId, { path: ['defaultWarehouseId'], message: 'Choose the warehouse the opening stock is in', ...always });
export type CreateItemInput = z.infer<typeof createItemSchema>;
export const updateItemSchema = itemRules(itemBase.extend({ version: z.number().int() }));
export type UpdateItemInput = z.infer<typeof updateItemSchema>;

/** Minimum / maximum / reorder quantities of many items at once (Items › Reorder levels). */
export const itemLevelsSchema = z.object({
  rows: z
    .array(
      z
        .object({ itemId: uuidReq('Item missing'), minQty: optAmount('minimum quantity'), maxQty: optAmount('maximum quantity'), reorderQty: optAmount('reorder quantity') })
        .refine((v) => v.minQty == null || v.maxQty == null || v.maxQty >= v.minQty, { path: ['maxQty'], message: 'Maximum must be at least the minimum quantity', ...always }),
    )
    .min(1, 'Nothing to save')
    .max(2000, 'At most 2,000 items at a time'),
});
export type ItemLevelsInput = z.infer<typeof itemLevelsSchema>;

/** Marks every active item of a category (and its sub-categories) as general or not. */
export const itemGeneralByCategorySchema = z.object({ categoryId: uuidReq('Choose a category'), isGeneral: z.boolean() });
export type ItemGeneralByCategoryInput = z.infer<typeof itemGeneralByCategorySchema>;

/** One row of Items › Reorder levels. */
export interface ItemLevelRow {
  id: string;
  code: string;
  name: string;
  category: string;
  uom: string;
  onHand: number;
  minQty: number | null;
  maxQty: number | null;
  reorderQty: number | null;
}

// ---------- Bank accounts & cheque books ----------

export const bankAccountSchema = z.object({
  code: lookupCode,
  kind: z.enum(BANK_ACCOUNT_KINDS),
  bankName: req('Bank name', 120),
  accountName: req('Account name', 160),
  accountNumber: z.string().trim().regex(/^[0-9A-Za-z-]{0,34}$/, 'Letters, digits and dash only').optional().default(''),
  iban: ibanSchema.optional().default(''),
  swift: z.string().trim().toUpperCase().regex(/^([A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?)?$/, 'SWIFT/BIC is 8 or 11 characters').optional().default(''),
  branchName: opt(120),
  currency: z.literal('AED').default('AED'),
  /** Ledger account (kind Bank) this bank account posts to. */
  glAccountId: z.string().uuid().nullable().optional().default(null),
  isActive: z.boolean(),
});
export type BankAccountInput = z.infer<typeof bankAccountSchema>;

/** A field's place on the cheque, in mm from the cheque's top-left corner; `w` is the width it may fill. */
const chequeSpot = z.object({ x: z.number().min(0).max(250), y: z.number().min(0).max(120), w: z.number().min(10).max(250) });
/**
 * Where each field prints on one bank's cheques (owner, 2026-10-29). Measured once from a blank cheque, checked with a test
 * print on plain paper held against a real cheque; the printer nudge moves everything together.
 */
export const chequeLayoutSchema = z.object({
  widthMm: z.number().min(100, 'At least 100 mm').max(250, 'At most 250 mm'),
  heightMm: z.number().min(50, 'At least 50 mm').max(120, 'At most 120 mm'),
  nudgeXMm: z.number().min(-30).max(30),
  nudgeYMm: z.number().min(-30).max(30),
  fontPt: z.number().min(8).max(16),
  dateStyle: z.enum(['SLASHES', 'BOXES']),
  /** BOXES: the distance between the centres of two date boxes (the eight boxes D D M M Y Y Y Y). */
  dateBoxMm: z.number().min(3).max(12),
  date: chequeSpot,
  payee: chequeSpot,
  words: chequeSpot,
  /** Where the amount in words continues when it does not fit on the first line. */
  words2: chequeSpot,
  figures: chequeSpot,
  acPayee: z.boolean(),
  acPayeeSpot: chequeSpot,
});
export type ChequeLayout = z.infer<typeof chequeLayoutSchema>;
/** A starting point: a common UAE cheque. Every bank's cheque is measured and adjusted before the first print. */
export const DEFAULT_CHEQUE_LAYOUT: ChequeLayout = {
  widthMm: 178, heightMm: 80, nudgeXMm: 0, nudgeYMm: 0, fontPt: 11, dateStyle: 'BOXES', dateBoxMm: 5,
  date: { x: 134, y: 9, w: 40 }, payee: { x: 20, y: 24, w: 150 }, words: { x: 26, y: 33, w: 144 }, words2: { x: 8, y: 41, w: 118 },
  figures: { x: 136, y: 41, w: 36 }, acPayee: true, acPayeeSpot: { x: 8, y: 6, w: 30 },
};

export const chequeBookSchema = z
  .object({
    bankAccountId: uuidReq('Select a bank account'),
    name: opt(40),
    firstNumber: z.coerce.number({ error: 'Enter the first cheque number' }).int('Whole number').min(1, 'At least 1').max(1_999_999_999, 'Too long for a cheque number'),
    lastNumber: z.coerce.number({ error: 'Enter the last cheque number' }).int('Whole number').min(1, 'At least 1').max(1_999_999_999, 'Too long for a cheque number'),
    numberLength: z.coerce.number().int().min(4, 'At least 4 digits').max(10, 'At most 10 digits').default(6),
    receivedOn: isoDate,
    remarks: opt(300),
    isActive: z.boolean(),
  })
  .refine((v) => v.lastNumber >= v.firstNumber, { path: ['lastNumber'], message: 'Last number must not be below the first', ...always })
  .refine((v) => v.lastNumber - v.firstNumber < 1000, { path: ['lastNumber'], message: 'A cheque book holds at most 1,000 leaves', ...always })
  .refine((v) => String(v.lastNumber).length <= v.numberLength, { path: ['numberLength'], message: 'Too short for the last cheque number', ...always });
export type ChequeBookInput = z.infer<typeof chequeBookSchema>;

// ---------- Employees (core record; payroll details come with HR & Payroll) ----------

/** Today's date in the UAE (UTC+4), as YYYY-MM-DD. */
export const todayUae = () => new Date(Date.now() + 4 * 3_600_000).toISOString().slice(0, 10);
const today = todayUae;
const yearsBetween = (a: string, b: string) => {
  const [ya, ma, da] = a.split('-').map(Number) as [number, number, number];
  const [yb, mb, db] = b.split('-').map(Number) as [number, number, number];
  return yb - ya - (mb < ma || (mb === ma && db < da) ? 1 : 0);
};

export const PAY_METHODS = ['BANK', 'CASH', 'CHEQUE'] as const;
export type PayMethod = (typeof PAY_METHODS)[number];
export const PAY_METHOD_LABELS: Record<PayMethod, string> = { BANK: 'Bank (WPS)', CASH: 'Cash', CHEQUE: 'Cheque' };

const employeeBase = z.object({
  fullName: req('Full name', 120),
  fatherName: opt(120),
  gender: z.enum(GENDERS, { error: 'Select gender' }),
  dateOfBirth: optDate,
  maritalStatus: z.enum(MARITAL_STATUSES).nullable().optional().transform((v) => v ?? null),
  nationality: z.string().trim().regex(/^([A-Z]{2})?$/, 'Select a nationality').optional().default(''),
  nationalIdNo: opt(40),
  emiratesId: emiratesIdSchema.optional().default(''),
  emiratesIdExpiry: optDate,
  passportNo: z.string().trim().regex(/^([A-Z0-9]{5,15})?$/i, 'Passport number is 5–15 letters or digits').optional().default(''),
  passportExpiry: optDate,
  visaType: z.enum(VISA_TYPES).nullable().optional().transform((v) => v ?? null),
  visaExpiry: optDate,
  labourCardNo: opt(30),
  labourCardExpiry: optDate,
  mobile: phoneSchema.optional().default(''),
  email: optionalEmail,
  address: opt(400),
  departmentId: uuidReq('Select a department'),
  designationId: uuidOpt,
  branchId: uuidReq('Select a branch'),
  costCentreId: uuidReq('Select a cost centre'),
  labourType: z.enum(LABOUR_TYPES, { error: 'Select direct or indirect labour' }),
  hireDate: isoDate,
  status: z.enum(EMPLOYEE_STATUSES),
  terminationDate: optDate,
  userId: uuidOpt,
  /** How net pay is paid; null = not set. */
  payMethod: z.enum(PAY_METHODS).nullable().optional().default(null),
  bankName: opt(80),
  /** UAE IBAN: AE + 21 digits (spaces allowed when typed). */
  iban: z.string().trim().transform((v) => v.replace(/\s/g, '').toUpperCase()).pipe(z.string().regex(/^(AE\d{21})?$/, 'A UAE IBAN is AE followed by 21 digits')).optional().default(''),
  bankAccountName: opt(120),
  /** End of probation; UAE law allows at most 6 months from the hire date. */
  probationEndDate: optDate,
  emergencyName: opt(120),
  emergencyRelation: opt(60),
  emergencyPhone: phoneSchema.optional().default(''),
});

/** The date n months after an ISO date (same day, or the month's last day). */
const addMonthsIso = (d: string, n: number) => {
  const [y, m, day] = d.split('-').map(Number) as [number, number, number];
  const last = new Date(Date.UTC(y, m - 1 + n + 1, 0)).getUTCDate();
  return new Date(Date.UTC(y, m - 1 + n, Math.min(day, last))).toISOString().slice(0, 10);
};

const employeeRules = <T extends z.ZodType<z.infer<typeof employeeBase>>>(s: T) =>
  s
    .refine((v) => !v.dateOfBirth || v.dateOfBirth <= today(), { path: ['dateOfBirth'], message: 'The date of birth cannot be in the future', ...always })
    .refine((v) => !v.hireDate || v.hireDate <= today(), { path: ['hireDate'], message: 'The hire date cannot be in the future; add the employee on or after their first day', ...always })
    .refine((v) => !v.probationEndDate || !v.hireDate || v.probationEndDate > v.hireDate, { path: ['probationEndDate'], message: 'Probation ends after the hire date', ...always })
    .refine((v) => !v.probationEndDate || !v.hireDate || v.probationEndDate <= addMonthsIso(v.hireDate, 6), { path: ['probationEndDate'], message: 'UAE law allows at most 6 months of probation from the hire date', ...always })
    .refine((v) => !v.emergencyPhone || !!v.emergencyName, { path: ['emergencyName'], message: 'Whose number is it?', ...always })
    .refine((v) => v.status !== 'LEFT' || !!v.terminationDate, { path: ['terminationDate'], message: 'Enter the last working day', ...always })
    .refine((v) => v.status !== 'ACTIVE' || !v.terminationDate || v.terminationDate >= today(), {
      path: ['status'],
      message: 'An employee whose last working day has passed must be marked as left',
      ...always,
    })
    .refine((v) => !v.terminationDate || !v.hireDate || v.terminationDate >= v.hireDate, {
      path: ['terminationDate'],
      message: 'Cannot be before the hire date',
      ...always,
    })
    .refine((v) => !v.dateOfBirth || !v.hireDate || yearsBetween(v.dateOfBirth, v.hireDate) >= 15, {
      path: ['dateOfBirth'],
      message: 'Employee must be at least 15 years old on the hire date',
      ...always,
    })
    .refine((v) => !v.emiratesIdExpiry || !!v.emiratesId, { path: ['emiratesId'], message: 'Enter the Emirates ID for this expiry date', ...always })
    .refine((v) => !v.passportExpiry || !!v.passportNo, { path: ['passportNo'], message: 'Enter the passport number for this expiry date', ...always })
    .refine((v) => v.payMethod !== 'BANK' || !!v.iban, { path: ['iban'], message: 'Enter the IBAN to pay by bank', ...always });

export const createEmployeeSchema = employeeRules(employeeBase);
export type CreateEmployeeInput = z.infer<typeof createEmployeeSchema>;
export const updateEmployeeSchema = employeeRules(employeeBase.extend({ version: z.number().int() }));
export type UpdateEmployeeInput = z.infer<typeof updateEmployeeSchema>;

// ---------- Response types ----------

export interface Ref {
  id: string;
  code: string;
  name: string;
}

/** Row of a simple lookup list (units, cost centres, VAT codes, …). Extra columns per kind. */
export interface LookupRow {
  id: string;
  code: string;
  name: string;
  isActive: boolean;
  [extra: string]: unknown;
}

/** Lookup entry for selects; inactive entries are kept so existing values still display. */
export interface LookupRef extends Ref {
  isActive: boolean;
}

export interface MasterLookups {
  uoms: (LookupRef & { decimals: number })[];
  costCentres: LookupRef[];
  taxCodes: (LookupRef & { kind: TaxKind; rate: number })[];
  paymentTerms: (LookupRef & { kind: PaymentTermKind; days: number })[];
  warehouses: (LookupRef & { type: WarehouseType; branchId: string })[];
  categories: (LookupRef & { kind: CategoryKind; parentId: string | null; costCentreId: string | null; path: string })[];
  salespersons: LookupRef[];
  designations: (LookupRef & { departmentId: string | null; rank: number })[];
  /** Production & Inventory sections (not HR departments): who uses material and runs operations. */
  productionSections: (LookupRef & { oldNo: number | null; departmentIds: string[] })[];
  /** In hierarchy order; `depth` 0 at the top, `path` sorts in tree order. */
  departments: (LookupRef & { parentId: string | null; depth: number; path: string })[];
  /** employees: active staff in the branch (HR shows Branch only when more than one branch has staff). */
  branches: (LookupRef & { employees: number })[];
  bankAccounts: (LookupRef & { kind: BankAccountKind })[];
  /** Heads a new supplier's own account can go under (Trade Creditors, 202…; owner 2026-10-27). */
  supplierHeads: { code: string; name: string }[];
}

export interface PartnerListItem {
  id: string;
  code: string;
  name: string;
  trn: string;
  phone: string;
  email: string;
  emirate: Emirate | null;
  isCustomer: boolean;
  isSupplier: boolean;
  customerType: CustomerType | null;
  vatScenario: VatScenario | null;
  supplierGroup: SupplierGroup | null;
  paymentTerm: string | null;
  salesperson: string | null;
  status: MasterStatus;
  legacyCodes: string[];
  /** Ledger balance on the role's control accounts (debit positive: customer owes / advance paid to supplier). */
  balance: number;
  /** This year: invoiced to the customer, or billed by the supplier. */
  yearToDate: number;
}

export interface PartnerDetail extends Omit<CreatePartnerInput, 'contacts'> {
  id: string;
  code: string;
  status: MasterStatus;
  mergedInto: Ref | null;
  legacyCodes: string[];
  contacts: (PartnerContactInput & { id: string })[];
  version: number;
  createdAt: string;
  updatedAt: string;
}

export interface DuplicateGroup {
  reason: string;
  records: { id: string; code: string; name: string; detail: string; legacyCodes: string[] }[];
}

export interface ItemListItem {
  id: string;
  code: string;
  name: string;
  type: ItemType;
  category: string;
  product: string | null;
  uom: string;
  costCentre: string;
  isStockTracked: boolean;
  isGeneral: boolean;
  status: MasterStatus;
  aliases: string[];
  /** Stock on hand (all warehouses), average cost and value. */
  onHand: number;
  avgCost: number;
  value: number;
}

/** Item page side panel: where the stock is and the last purchase. */
export interface ItemStockInfo {
  onHand: number;
  avgCost: number;
  value: number;
  byWarehouse: { warehouse: string; qty: number }[];
  lastPurchase: { date: string; number: string | null; supplier: string | null; rate: number } | null;
  lastMovement: string | null;
}

export interface ItemDetail extends CreateItemInput {
  id: string;
  code: string;
  status: MasterStatus;
  mergedInto: Ref | null;
  aliases: { kind: ItemAliasKind; value: string }[];
  version: number;
  createdAt: string;
  updatedAt: string;
}

export interface ProductListItem {
  id: string;
  code: string;
  name: string;
  category: string;
  costCentre: string;
  legacyCode: string | null;
  itemCount: number;
  isActive: boolean;
  version: number;
  categoryId: string;
  costCentreId: string;
  description: string;
}

export interface SalespersonItem extends SalespersonInput {
  id: string;
  code: string;
  employeeName: string | null;
  userName: string | null;
  legacyAccountCode: string | null;
  account: { id: string; code: string; name: string } | null;
  partner: { id: string; code: string; name: string } | null;
  version: number;
}

export interface BankAccountItem extends BankAccountInput {
  id: string;
  legacyAccountCode: string | null;
  glAccount: { id: string; code: string; name: string; balance: number } | null;
  chequeBooks: number;
  version: number;
}

export interface ChequeBookItem extends ChequeBookInput {
  id: string;
  bankAccount: string;
  leaves: number;
  version: number;
}

export interface EmployeeListItem {
  id: string;
  empNo: string;
  fullName: string;
  department: string;
  designation: string | null;
  branch: string;
  labourType: (typeof LABOUR_TYPES)[number];
  mobile: string;
  hireDate: string | null;
  status: EmployeeStatus;
  /** Nearest document expiry (Emirates ID, passport, visa, labour card) within 60 days or past. */
  expiring: { document: string; date: string } | null;
  photoAt: string | null;
  probationEndDate: string | null;
  /** Today's shift and machine User ID; does not punch. */
  shift: string | null;
  shiftTimes: { start: string; end: string } | null;
  machineId: string | null;
  attendanceExempt: boolean;
  /** Today's pay: only for those who may see salaries (else null). */
  basic: number | null;
  gross: number | null;
}

export interface EmployeeDetail extends CreateEmployeeInput {
  id: string;
  /** When the profile photo was last changed (null = no photo); also busts the image cache. */
  photoAt: string | null;
  empNo: string;
  version: number;
  createdAt: string;
  updatedAt: string;
}

// ---------- Product structure, attributes & catalogue (Phase 1) ----------

export const ATTRIBUTE_TYPES = ['TEXT', 'NUMBER', 'SELECT', 'BOOLEAN'] as const;
export type AttributeType = (typeof ATTRIBUTE_TYPES)[number];
export const ATTRIBUTE_TYPE_LABELS: Record<AttributeType, string> = { TEXT: 'Text', NUMBER: 'Number', SELECT: 'Choose from a list', BOOLEAN: 'Yes / no' };
export const ATTRIBUTE_LEVELS = ['PRODUCT', 'VARIANT'] as const;
export type AttributeLevel = (typeof ATTRIBUTE_LEVELS)[number];

export const productAttributeSchema = z
  .object({
    code: z.string().trim().toUpperCase().regex(/^[A-Z0-9_]{2,30}$/, 'Letters, digits and _ (2–30)'),
    name: req('Name', 80),
    type: z.enum(ATTRIBUTE_TYPES),
    level: z.enum(ATTRIBUTE_LEVELS),
    unit: opt(20).default(''),
    options: z.array(z.string().trim().min(1).max(60)).max(60).default([]),
    categoryIds: z.array(z.string().uuid()).max(100).default([]),
    sortOrder: z.coerce.number().int().min(0).max(999).default(0),
    isActive: z.boolean().default(true),
  })
  .superRefine((v, ctx) => {
    if (v.type === 'SELECT' && v.options.length < 2) ctx.addIssue({ code: 'custom', path: ['options'], message: 'Add at least two choices' });
    if (new Set(v.options.map((o) => o.toLowerCase())).size !== v.options.length) ctx.addIssue({ code: 'custom', path: ['options'], message: 'Each choice once' });
  });
export type ProductAttributeInput = z.infer<typeof productAttributeSchema>;

export interface ProductAttributeItem {
  id: string;
  code: string;
  name: string;
  type: AttributeType;
  level: AttributeLevel;
  unit: string;
  options: string[];
  categoryIds: string[];
  sortOrder: number;
  isActive: boolean;
  usage: number;
  version: number;
}

export const variantAttributesSchema = z.object({ attributes: z.record(z.string().uuid(), z.string().max(200)) });

export interface ProductTreeNode {
  id: string;
  code: string;
  name: string;
  isActive: boolean;
  /** Sub-categories of a main category. */
  children: ProductTreeNode[];
  products: number;
  variants: number;
}

export interface ProductListRow {
  id: string;
  code: string;
  name: string;
  categoryId: string;
  category: string;
  mainCategory: string | null;
  collection: string;
  isCustom: boolean;
  isActive: boolean;
  variants: number;
  catalogued: boolean;
}

export interface ProductDetail {
  id: string;
  code: string;
  name: string;
  categoryId: string;
  /** Sub-category → main category, for the breadcrumb. */
  path: { id: string; name: string }[];
  costCentreId: string;
  description: string;
  collection: string;
  material: string;
  finish: string;
  isCustom: boolean;
  installation: boolean | null;
  /** What the category (or its parents) says when the product does not decide. */
  categoryInstallation: boolean;
  warrantyMonths: number | null;
  /** Warranty the category (or its parents, then the setting) gives when the product does not decide. */
  categoryWarranty: number;
  isActive: boolean;
  version: number;
  /** Attributes that apply to this product's category, with this product's values. */
  attributes: (ProductAttributeItem & { value: string })[];
  variants: { id: string; code: string; name: string; status: string; lengthCm: number | null; widthCm: number | null; heightCm: number | null; colour: string; uom: string; standardSellingRate: number | null; onHand: number; attributes: Record<string, string> }[];
  catalogue: { id: string; code: string; name: string; status: string }[];
}

export const catalogueItemSchema = z.object({
  name: req('Catalogue name', 160),
  productId: uuidReq('Select the product'),
  collection: opt(80).default(''),
  marketingDescription: opt(4000).default(''),
  standardPrice: z.preprocess((v) => (v === '' || v == null ? null : v), z.coerce.number().min(0, 'Cannot be negative').max(10_000_000).nullable()).default(null),
  displaySize: opt(80).default(''),
  status: z.enum(['ACTIVE', 'INACTIVE']).default('ACTIVE'),
  variants: z
    .array(z.object({ itemId: z.string().uuid(), price: z.preprocess((v) => (v === '' || v == null ? null : v), z.coerce.number().min(0).max(10_000_000).nullable()).default(null) }))
    .max(500)
    .default([]),
});
export type CatalogueItemInput = z.infer<typeof catalogueItemSchema>;

export interface CatalogueCard {
  id: string;
  code: string;
  name: string;
  product: { id: string; code: string; name: string };
  category: string;
  mainCategory: string | null;
  collection: string;
  standardPrice: number | null;
  displaySize: string;
  displayOrder: number;
  status: 'ACTIVE' | 'INACTIVE';
  variants: number;
  imageId: string | null;
}

export interface CatalogueDetail extends Omit<CatalogueCard, 'variants'> {
  marketingDescription: string;
  version: number;
  variants: { itemId: string; code: string; name: string; price: number | null; standardSellingRate: number | null }[];
}

/**
 * Production & Inventory sections (owner, 2026-11-03): their own list, used by requisitions, issues, returns, order
 * material, operations and mattress stages. Workers of a section are the people of its HR departments.
 */
export const productionSectionSchema = z.object({
  code: z.string().trim().toUpperCase().min(1, 'Code is required').max(12, 'At most 12 characters').regex(/^[A-Z0-9_-]+$/, 'Letters, digits, - and _'),
  name: req('Name', 60),
  oldNo: z.preprocess((v) => (v === '' || v == null ? null : v), z.coerce.number().int().min(0).max(9999).nullable()).default(null),
  departmentIds: z.array(z.string().uuid()).max(30).default([]),
  sortOrder: z.coerce.number().int().min(0).max(9999).default(0),
  isActive: z.boolean(),
});
export type ProductionSectionInput = z.infer<typeof productionSectionSchema>;
